import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("admin.login_fail"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen flex items-center justify-center px-5 grid-bg">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo height="h-11" /></div>
        <div className="flex justify-center items-center gap-3 mb-8"><LanguageSwitcher /><ThemeToggle /></div>
        <div className="border border-white/10 bg-[#0C0C0E] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 flex items-center justify-center border border-[#E60000]/40 bg-[#E60000]/10">
              <Lock className="w-4 h-4 text-[#E60000]" />
            </div>
            <div>
              <h1 className="font-display font-900 text-white text-xl tracking-tight uppercase">{t("admin.login_title")}</h1>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("admin.login_sub")}</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("admin.email")}</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" placeholder="admin@garageterspelt.be" className="bg-[#050505] border-white/10 rounded-none h-12 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
            </div>
            <div>
              <label className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">{t("admin.password")}</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" placeholder="••••••••" className="bg-[#050505] border-white/10 rounded-none h-12 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
            </div>
            {error && <p className="text-[#FF1A1A] text-sm font-mono-tech" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full inline-flex items-center justify-center gap-2 h-12 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.signin")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
